import { atom } from 'jotai'
import { dequal } from 'dequal'
import { withHistory, UNDO, REDO, RESET } from 'jotai-history'

import { applyAutoSelectVariants } from 'Automations/nodeDefinitions/helpers/applyAutoSelectVariants/applyAutoSelectVariants'
import {
  dedupeEdges,
  pruneOrphanedEdges,
  resolvedNodeDefinitionsAtom
} from 'Automations/nodeDefinitions'
import type { Config as ReminderConfig } from 'Automations/nodeDefinitions/definitions/reminder'
import {
  AutomationsPresentationV1,
  AutomationsPresentationV1Node
} from './types'
import {
  applyReminderRecipientDefaults,
  pickReminderRecipients
} from './helpers/reminderRecipientPrefill'

export { UNDO, REDO, RESET }

/**
 * Presentation
 *
 * _presentationAtom: Full state for React Flow (includes selected, measured, etc.)
 * _historySourceAtom: Clean state (only id, type, position, data for nodes + edges)
 *
 * History tracks _historySourceAtom so React Flow runtime mutations (selection,
 * measured dimensions, etc.) never create history entries. On undo/redo we apply
 * the historical clean state directly to _presentationAtom, bypassing the
 * presentationAtom setter so no new history entry is created.
 */
const _presentationAtom = atom<AutomationsPresentationV1>()
const _historySourceAtom = atom<AutomationsPresentationV1>()

/**
 * Extract only the presentation-owned properties from nodes, stripping
 * React Flow runtime properties (selected, measured, width, height, etc.)
 */
const toPresentationOnly = (
  presentation: AutomationsPresentationV1 | undefined
): AutomationsPresentationV1 | undefined => {
  if (!presentation) return presentation
  return {
    ...presentation,
    nodes: presentation.nodes.map(
      ({ id, type, position, data }): AutomationsPresentationV1Node => ({
        id,
        type,
        position,
        data
      })
    )
  }
}

/**
 * Debounced history sync using the atomWithDebounce pattern from jotai.
 * Multiple rapid changes (e.g. React Flow fires edge removal then node
 * removal within the same user action) collapse into a single history entry.
 *
 * @see https://jotai.org/docs/recipes/atom-with-debounce
 */
const HISTORY_DEBOUNCE_MS = 100
const _prevTimeoutAtom = atom<ReturnType<typeof setTimeout> | undefined>(
  undefined
)
const _pendingNewPresentationHistoryAtom = atom<
  AutomationsPresentationV1 | undefined
>(undefined)

const _debouncedHistorySyncAtom = atom(
  null,
  (get, set, fullState: AutomationsPresentationV1 | undefined) => {
    clearTimeout(get(_prevTimeoutAtom))
    set(_pendingNewPresentationHistoryAtom, fullState)

    const timeoutId = setTimeout(() => {
      if (
        !dequal(
          toPresentationOnly(fullState),
          toPresentationOnly(get(_historySourceAtom))
        )
      ) {
        set(_historySourceAtom, fullState)
      }
      set(_pendingNewPresentationHistoryAtom, undefined)
      set(_prevTimeoutAtom, undefined)
    }, HISTORY_DEBOUNCE_MS)

    set(_prevTimeoutAtom, timeoutId)
  }
)

/**
 * Flush any pending debounced history sync immediately.
 * Used by undo/redo to ensure we have the latest state before navigating history.
 */
const _flushHistorySyncAtom = atom(null, (get, set) => {
  clearTimeout(get(_prevTimeoutAtom))
  set(_prevTimeoutAtom, undefined)

  const pending = get(_pendingNewPresentationHistoryAtom)
  if (
    pending &&
    !dequal(
      toPresentationOnly(pending),
      toPresentationOnly(get(_historySourceAtom))
    )
  ) {
    set(_historySourceAtom, pending)
  }
  set(_pendingNewPresentationHistoryAtom, undefined)
})

export const presentationAtom = atom(
  get => get(_presentationAtom),
  (get, set, update: AutomationsPresentationV1 | undefined) => {
    if (!update) {
      set(_presentationAtom, update)
      return
    }

    /**
     * Deleting a node can leave edges pointing at it. Prune before anything
     * reads them, so no processor and no stored state sees a phantom branch.
     *
     * Every edit to the canvas lands here, so this is also where a connection
     * stored twice is dropped — whichever editor gesture wrote it.
     */
    const edges = dedupeEdges(pruneOrphanedEdges(update))

    // The presentation can be set before the resolved registry has loaded
    // (e.g. initial load runs before `resolveNodeDefinitions` settles). Auto-
    // selecting placeholder variants is an optimisation, so skip it until the
    // registry is ready rather than gating the whole presentation on it.
    const definitions = get(resolvedNodeDefinitionsAtom)
    const autoSelectedNodes = definitions
      ? applyAutoSelectVariants({
          nodes: update.nodes,
          edges,
          definitions
        })
      : update.nodes

    // When a new connection is added (drag, drop-in or insert), fill the
    // reminders downstream of it from their closest upstream reminder
    // (BADGE-5974) — so the newly-connected reminder and any
    // already-connected-but-default reminders after it inherit the addressing.
    const nodes = definitions
      ? applyReminderRecipientDefaults({
          nodes: autoSelectedNodes,
          edges,
          previousEdges: get(_presentationAtom)?.edges,
          defaultRecipients: pickReminderRecipients(
            definitions.REMINDER.defaultConfig as ReminderConfig
          )
        })
      : autoSelectedNodes

    const processed = { ...update, nodes, edges }
    set(_presentationAtom, processed)

    const currentHistory = get(_historySourceAtom)
    if (!currentHistory) {
      set(_historySourceAtom, processed)
      return
    }

    const clean = toPresentationOnly(processed)
    if (!dequal(clean, toPresentationOnly(currentHistory))) {
      set(_debouncedHistorySyncAtom, processed)
    }
  }
)

export const presentationHistoryAtom = withHistory(_historySourceAtom, 100)

/**
 * Flush any pending debounced writes, then RESET history.
 * This ensures the debounced sync doesn't re-create a history entry after
 * the RESET fires.
 */
export const resetPresentationHistoryAtom = atom(null, (_get, set) => {
  set(_flushHistorySyncAtom)
  set(presentationHistoryAtom, RESET)
})

export const lastSavedPresentationAtom = atom<AutomationsPresentationV1>()

/**
 * Undo/redo derived atoms.
 * canUndo/canRedo read directly from history — since history only tracks
 * meaningful changes, no additional filtering is needed.
 */
export const canUndoAtom = atom(get => {
  return get(presentationHistoryAtom).canUndo
})

export const canRedoAtom = atom(get => {
  return get(presentationHistoryAtom).canRedo
})

/**
 * Incremented on every undo/redo navigation. Views that hold component-local
 * state derived from the presentation (e.g. uncontrolled tiptap editors in
 * node config forms) key off this so they remount and re-read the reverted
 * state — otherwise they keep displaying the undone values and can silently
 * re-commit them on the next edit.
 */
const _historyNavigationCountAtom = atom(0)
export const historyNavigationCountAtom = atom(get =>
  get(_historyNavigationCountAtom)
)

export const undoAtom = atom(null, (get, set) => {
  set(_flushHistorySyncAtom)
  if (!get(canUndoAtom)) return
  set(presentationHistoryAtom, UNDO)
  set(_historyNavigationCountAtom, count => count + 1)
  const historicalState = get(_historySourceAtom)
  if (historicalState) {
    set(_presentationAtom, historicalState)
  }
})

export const redoAtom = atom(null, (get, set) => {
  set(_flushHistorySyncAtom)
  if (!get(canRedoAtom)) return
  set(presentationHistoryAtom, REDO)
  set(_historyNavigationCountAtom, count => count + 1)
  const historicalState = get(_historySourceAtom)
  if (historicalState) {
    set(_presentationAtom, historicalState)
  }
})

/**
 * During drag, stores intermediate node positions without writing to presentationAtom.
 * nodesAtom getter checks this first, so React Flow sees live positions.
 * On drag end, this is cleared and final positions are committed to presentationAtom.
 */
export const draggingNodesAtom = atom<AutomationsPresentationV1Node[] | null>(
  null
)
